import type {
  CoverageRunDto,
  CoverageRunFailureKind,
  CoverageRunFailureSubkind,
} from '@flowqa/shared';

export type CoverageRunFailurePresentation = {
  kind: CoverageRunFailureKind;
  label: string;
  summary: string;
  subkind: string | null;
  subkindLabel: string | null;
  detail: string | null;
  nextStep: string | null;
};

type CoverageRunFailureInput = Pick<
  CoverageRunDto,
  'error' | 'failureKind' | 'failureSubkind' | 'status'
>;

const fallbackSummaryByKind: Record<CoverageRunFailureKind, string> = {
  duplicate: 'This coverage run matches an existing upload.',
  limit: 'Execution limit reached. Upgrade to process more coverage.',
  parse: 'We could not parse this coverage report.',
  partial: 'Some coverage data was imported, but one or more files failed.',
  permission: 'This coverage run needs the right workspace or account access.',
  retry: 'Coverage processing is temporarily unavailable. Please try again.',
  unknown: 'Coverage processing failed. Please try again.',
  validation: 'Coverage processing failed due to an invalid file.',
};

const labelByKind: Record<CoverageRunFailureKind, string> = {
  duplicate: 'Duplicate coverage run',
  limit: 'Plan limit',
  parse: 'Parse issue',
  partial: 'Partial coverage import',
  permission: 'Permission issue',
  retry: 'Retry suggested',
  unknown: 'Coverage run failed',
  validation: 'Validation issue',
};

const coverageRunFailureKinds = [
  'duplicate',
  'limit',
  'parse',
  'partial',
  'permission',
  'retry',
  'unknown',
  'validation',
] satisfies CoverageRunFailureKind[];

const coverageRunFailureSubkinds = [
  'checksum_mismatch',
  'content_type_mismatch',
  'duplicate_upload',
  'email_verification_required',
  'execution_limit',
  'file_name',
  'file_too_large',
  'invalid_artifact',
  'invalid_payload',
  'parser_error',
  'payload_empty',
  'project_token_mismatch',
  'queue_unavailable',
  'results_file_parse_failed',
  'unknown',
] satisfies CoverageRunFailureSubkind[];

const nextStepByKind: Record<CoverageRunFailureKind, string | null> = {
  duplicate: 'Check whether the existing upload already contains the coverage data you need.',
  limit: 'Review the workspace plan or reduce the coverage upload volume before trying again.',
  parse: 'Upload a valid Istanbul, LCOV, Cobertura, or supported coverage report.',
  partial: 'Open the imported coverage files and re-upload only the files that failed.',
  permission: 'Confirm the upload token, project access, and account verification before retrying.',
  retry: 'Wait a moment, then retry the coverage upload.',
  unknown: null,
  validation: 'Fix the file name, file type, or file contents before uploading again.',
};

const normaliseMessage = (message: string | null | undefined): string =>
  message?.trim().toLowerCase() ?? '';

const formatFailureSubkindLabel = (subkind: string | null | undefined): string | null => {
  const normalisedSubkind = subkind?.trim().replace(/[_-]+/g, ' ') ?? '';
  if (normalisedSubkind.length === 0) return null;

  return `${normalisedSubkind.charAt(0).toUpperCase()}${normalisedSubkind.slice(1)}`;
};

export const coverageRunFailureKindFilterOptions = coverageRunFailureKinds.map((value) => ({
  value,
  label: labelByKind[value],
}));

export const coverageRunFailureSubkindFilterOptions = coverageRunFailureSubkinds.map((value) => ({
  value,
  label: formatFailureSubkindLabel(value) ?? value,
}));

const resolveFailureKind = (coverageRun: CoverageRunFailureInput): CoverageRunFailureKind => {
  if (coverageRun.failureKind) return coverageRun.failureKind;

  const message = normaliseMessage(coverageRun.error);
  if (message.length === 0) return 'unknown';
  if (message.includes('checksum mismatch')) {
    return 'validation';
  }
  if (
    message.includes('duplicate') ||
    message.includes('idempotency') ||
    message.includes('checksum') ||
    message.includes('already exists')
  ) {
    return 'duplicate';
  }
  if (
    message.includes('verify your email') ||
    message.includes('permission') ||
    message.includes('forbidden') ||
    message.includes('not authorised') ||
    message.includes('not authorized') ||
    message.includes('token does not match project')
  ) {
    return 'permission';
  }
  if (
    message.includes('execution limit') ||
    message.includes('limit reached') ||
    message.includes('upgrade to upload') ||
    message.includes('quota')
  ) {
    return 'limit';
  }
  if (
    message.includes('temporarily unavailable') ||
    message.includes('timed out') ||
    message.includes('try again') ||
    message.includes('queue')
  ) {
    return 'retry';
  }
  if (
    message.includes('invalid') ||
    message.includes('content type') ||
    message.includes('file extension') ||
    message.includes('file name') ||
    message.includes('too large') ||
    message.includes('empty')
  ) {
    return 'validation';
  }
  if (
    message.includes('coverage') ||
    message.includes('parse') ||
    message.includes('parser') ||
    message.includes('istanbul') ||
    message.includes('lcov') ||
    message.includes('cobertura') ||
    message.includes('json') ||
    message.includes('xml')
  ) {
    return 'parse';
  }
  return 'unknown';
};

export const resolveCoverageRunFailurePresentation = (
  coverageRun: CoverageRunFailureInput,
): CoverageRunFailurePresentation | null => {
  if (coverageRun.status !== 'failed') return null;

  const kind = resolveFailureKind(coverageRun);
  const detail = coverageRun.error?.trim() ?? '';
  const subkind = coverageRun.failureSubkind?.trim() ?? '';
  return {
    kind,
    label: labelByKind[kind],
    summary: fallbackSummaryByKind[kind],
    subkind: subkind.length > 0 ? subkind : null,
    subkindLabel: formatFailureSubkindLabel(coverageRun.failureSubkind),
    detail: detail.length > 0 ? detail : null,
    nextStep: nextStepByKind[kind],
  };
};
